/**
 * Компонент области ввода сообщений
 * Поддерживает прикрепление до 3 изображений с preview
 */

import { useState, useRef, useCallback, useMemo, useEffect, useLayoutEffect, ChangeEvent, KeyboardEvent } from 'react';
import { Paperclip, Mic, Send, X, ImagePlus } from 'lucide-react';
import styles from './InputArea.module.css';
import { validateMessage, validateFile, formatFileSize } from '@/utils/validation';
import { LIMITS } from '@/constants/defaults';
import { chatLogger } from '@/utils/chatLogger';

/**
 * Пропсы компонента InputArea
 */
interface InputAreaProps {
  /** Placeholder текста */
  placeholder: string;
  /** Колбек отправки сообщения (поддерживает массив файлов) */
  onSend: (text: string, files?: File[]) => void;
  /** Disabled состояние */
  disabled?: boolean;
  /** Показывать кнопку прикрепления файла */
  showAttach?: boolean;
  /** Показывать кнопку голосового ввода */
  showVoice?: boolean;
  /** Прогресс загрузки файла (0-100) */
  uploadProgress?: number;
  /** Колбек для ошибок (вместо alert) */
  onError?: (message: string) => void;
}

/**
 * Область ввода сообщений
 * 
 * @description Input с auto-resize, кнопками attach и send.
 * Поддерживает прикрепление до MAX_FILES_PER_MESSAGE изображений с preview.
 */
export function InputArea({
  placeholder,
  onSend,
  disabled = false,
  showAttach = true,
  showVoice = false,
  uploadProgress = 0,
  onError,
}: InputAreaProps) {
  const [text, setText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const isUploading = uploadProgress > 0 && uploadProgress < 100;
  const maxFiles = LIMITS.MAX_FILES_PER_MESSAGE;
  const prevDisabledRef = useRef(disabled);

  useLayoutEffect(() => {
    if (prevDisabledRef.current && !disabled) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    prevDisabledRef.current = disabled;
  }, [disabled]);

  // Auto-resize textarea
  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Обработка Enter (отправка)
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Показать ошибку (через колбек или console)
  const showValidationError = useCallback((message: string) => {
    if (onError) {
      onError(message);
    } else {
      chatLogger.warn('[InputArea]', message);
    }
  }, [onError]);

  // Обработка выбора файлов — добавляем в pending (не отправляем сразу)
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    addFiles(Array.from(selectedFiles));

    // Очищаем input для повторного выбора тех же файлов
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Добавление файлов с валидацией
  const addFiles = useCallback((newFiles: File[]) => {
    const remainingSlots = maxFiles - pendingFiles.length;
    
    if (remainingSlots <= 0) {
      showValidationError(`Максимум ${maxFiles} файла в одном сообщении`);
      return;
    }

    const filesToAdd: File[] = [];

    for (const file of newFiles) {
      if (filesToAdd.length >= remainingSlots) {
        showValidationError(`Можно добавить ещё ${remainingSlots} файл(а). Остальные пропущены.`);
        break;
      }

      const validation = validateFile(file);
      if (!validation.valid) {
        showValidationError(validation.error || 'Неподдерживаемый файл');
        continue;
      }

      // Проверяем дубликаты по имени и размеру
      const isDuplicate = pendingFiles.some(
        (existing) => existing.name === file.name && existing.size === file.size
      );
      if (isDuplicate) {
        continue;
      }

      filesToAdd.push(file);
    }

    if (filesToAdd.length > 0) {
      setPendingFiles((prev) => [...prev, ...filesToAdd]);
    }
  }, [pendingFiles, maxFiles, showValidationError]);

  // Удаление файла из pending
  const removeFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Отправка сообщения (текст + файлы)
  const handleSend = () => {
    const trimmedText = text.trim();

    // Нет текста и нет файлов — ничего не делаем
    if (!trimmedText && pendingFiles.length === 0) return;

    // Валидация текста (если есть)
    if (trimmedText) {
      const validation = validateMessage(trimmedText);
      if (!validation.valid) {
        showValidationError(validation.error || 'Ошибка валидации');
        return;
      }
    }

    onSend(trimmedText, pendingFiles.length > 0 ? pendingFiles : undefined);

    // Очистка
    setText('');
    setPendingFiles([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const previewUrls = useMemo(
    () => pendingFiles.map((file) => URL.createObjectURL(file)),
    [pendingFiles],
  );

  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const canSend = (text.trim() || pendingFiles.length > 0) && !disabled;
  const canAddMoreFiles = pendingFiles.length < maxFiles;

  return (
    <div className={styles.inputArea}>
      {/* Preview прикреплённых файлов */}
      {pendingFiles.length > 0 && (
        <div className={styles.filesPreview}>
          {pendingFiles.map((file, index) => (
            <div key={`${file.name}-${file.size}-${index}`} className={styles.filePreview}>
              <img
                src={previewUrls[index]}
                alt={file.name}
                className={styles.previewImage}
              />
              <span className={styles.previewName} title={file.name}>
                {file.name}
                <span className={styles.previewSize}>{formatFileSize(file.size)}</span>
              </span>
              <button
                className={styles.removeBtn}
                onClick={() => removeFile(index)}
                aria-label={`Удалить ${file.name}`}
                type="button"
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
          ))}

          {/* Кнопка "добавить ещё" если есть место */}
          {canAddMoreFiles && (
            <button
              className={styles.addMoreBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isUploading}
              type="button"
              aria-label="Добавить ещё фото"
            >
              <ImagePlus size={18} strokeWidth={2} />
              <span>Ещё ({pendingFiles.length}/{maxFiles})</span>
            </button>
          )}
        </div>
      )}

      {/* Input container */}
      <div className={styles.inputContainer}>
        {/* Кнопка прикрепления */}
        {showAttach && (
          <>
            <button
              className={styles.actionBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isUploading || !canAddMoreFiles}
              aria-label={
                isUploading 
                  ? `Загрузка ${uploadProgress}%` 
                  : !canAddMoreFiles 
                  ? `Максимум ${maxFiles} файла`
                  : 'Прикрепить файл'
              }
              type="button"
            >
              {isUploading ? (
                <span style={{ fontSize: '10px', fontWeight: 'bold' }}>{uploadProgress}%</span>
              ) : (
                <Paperclip size={20} strokeWidth={2} />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className={styles.fileInput}
              accept="image/*"
              onChange={handleFileSelect}
            />
          </>
        )}

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          placeholder={placeholder}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          aria-label="Введите сообщение"
        />

        {/* Кнопка голосового ввода */}
        {showVoice && (
          <button
            className={styles.actionBtn}
            disabled={disabled}
            aria-label="Голосовой ввод"
            type="button"
          >
            <Mic size={20} strokeWidth={2} />
          </button>
        )}

        {/* Кнопка отправки */}
        <button
          className={styles.sendBtn}
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Отправить"
          type="button"
        >
          <Send size={18} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
